import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, CalendarClock, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';

type Level = 1 | 2;

interface PlanningRule {
  id: number;
  level: Level;
  category: string;
  title: string;
  instruction: string;
  priority: number;
  isActive: boolean;
}

interface RuleForm {
  level: Level;
  category: string;
  title: string;
  instruction: string;
  priority: number;
  isActive: boolean;
}

const CATEGORIES: Record<Level, { value: string; label: string }[]> = {
  1: [
    { value: 'capacity', label: 'Capacidad de máquina' },
    { value: 'inventory', label: 'Inventario/material' },
    { value: 'route', label: 'Ruta de fabricación' },
    { value: 'maintenance', label: 'Paros programados' },
    { value: 'tooling', label: 'Mangas/anilox' },
  ],
  2: [
    { value: 'priority_date', label: 'Prioridad por fecha' },
    { value: 'priority_product_type', label: 'Tipo de producto' },
    { value: 'priority_customer', label: 'Cliente prioritario' },
    { value: 'grouping', label: 'Agrupación (colores/marca)' },
    { value: 'sequence', label: 'Secuencia/balance' },
  ],
};

const categoryLabel = (category: string) =>
  [...CATEGORIES[1], ...CATEGORIES[2]].find((c) => c.value === category)?.label ?? category;

const SECTIONS: { level: Level; title: string }[] = [
  { level: 1, title: 'Nivel 1 — Reglas duras' },
  { level: 2, title: 'Nivel 2 — Políticas' },
];

const emptyForm = (level: Level): RuleForm => ({
  level,
  category: CATEGORIES[level][0].value,
  title: '',
  instruction: '',
  priority: 100,
  isActive: true,
});

const readError = async (response: Response, fallback: string) => {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch {
    return fallback;
  }
};

export default function PlanningAgentRules() {
  const [rules, setRules] = useState<Record<Level, PlanningRule[]>>({ 1: [], 2: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<RuleForm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Guards against overlapping loads (e.g. StrictMode double effects): a single fetch is in flight at a time.
  const inFlight = useRef<Promise<void> | null>(null);

  const loadRules = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    const run = (async () => {
      setError(null);
      try {
        // One request (no level filter); split by level on the client.
        const response = await fetch('/api/planning/rules', { credentials: 'include' });
        if (!response.ok) throw new Error(await readError(response, 'Error al cargar las reglas'));
        const data = await response.json();
        const all: PlanningRule[] = data.rules ?? [];
        setRules({ 1: all.filter((r) => r.level === 1), 2: all.filter((r) => r.level === 2) });
      } catch (e) {
        console.error('Error loading planning rules:', e);
        setError(e instanceof Error ? e.message : 'Error al cargar las reglas');
      } finally {
        setIsLoading(false);
        inFlight.current = null;
      }
    })();
    inFlight.current = run;
    return run;
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const openNew = (level: Level) => {
    setEditingId(null);
    setForm(emptyForm(level));
    setFormError(null);
  };

  const openEdit = (rule: PlanningRule) => {
    setEditingId(rule.id);
    setForm({
      level: rule.level,
      category: rule.category,
      title: rule.title,
      instruction: rule.instruction,
      priority: rule.priority,
      isActive: rule.isActive,
    });
    setFormError(null);
  };

  const closeForm = () => {
    setForm(null);
    setEditingId(null);
    setFormError(null);
  };

  // PUT validates the full schema and resets omitted fields, so always send the full object.
  const saveRule = async (id: number | null, payload: RuleForm): Promise<string | null> => {
    try {
      const response = await fetch(id ? `/api/planning/rules/${id}` : '/api/planning/rules', {
        method: id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) return await readError(response, 'Error al guardar la regla');
      return null;
    } catch (e) {
      console.error('Error saving planning rule:', e);
      return 'Error al guardar la regla';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setIsSaving(true);
    setFormError(null);
    const err = await saveRule(editingId, {
      ...form,
      title: form.title.trim(),
      instruction: form.instruction.trim(),
    });
    setIsSaving(false);
    if (err) {
      setFormError(err);
      return;
    }
    closeForm();
    await loadRules();
  };

  const handleToggleActive = async (rule: PlanningRule) => {
    setError(null);
    const err = await saveRule(rule.id, {
      level: rule.level,
      category: rule.category,
      title: rule.title,
      instruction: rule.instruction,
      priority: rule.priority,
      isActive: !rule.isActive,
    });
    if (err) setError(err);
    await loadRules();
  };

  const handleDelete = async (id: number) => {
    setIsDeleting(true);
    setError(null);
    try {
      const response = await fetch(`/api/planning/rules/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) setError(await readError(response, 'Error al eliminar la regla'));
    } catch (e) {
      console.error('Error deleting planning rule:', e);
      setError('Error al eliminar la regla');
    } finally {
      setIsDeleting(false);
      setConfirmDeleteId(null);
    }
    await loadRules();
  };

  const selectClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Agente de Planeación
        </CardTitle>
        <CardDescription>
          Nivel 1: el agente nunca puede violarlas. Nivel 2: preferencias ordenadas por prioridad (1 = más
          importante). El Nivel 3 (objetivo y reglas de la corrida) se captura al optimizar en el Gantt.
          Si eliminas todas las reglas, las reglas predeterminadas se restauran automáticamente.
          Si todas las reglas están inactivas, el agente usa las reglas predeterminadas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        )}

        {isLoading ? (
          <p className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando reglas...
          </p>
        ) : (
          SECTIONS.map(({ level, title }) => (
            <div key={level} className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{title}</h3>
                <Button type="button" size="sm" onClick={() => openNew(level)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Nuevo
                </Button>
              </div>
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Prioridad</th>
                      <th className="px-3 py-2 font-medium">Categoría</th>
                      <th className="px-3 py-2 font-medium">Título</th>
                      <th className="px-3 py-2 font-medium">Instrucción</th>
                      <th className="px-3 py-2 font-medium">Activa</th>
                      <th className="px-3 py-2 font-medium text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules[level].length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                          Sin reglas en este nivel.
                        </td>
                      </tr>
                    )}
                    {rules[level].map((rule) => (
                      <tr key={rule.id} className={`border-t align-top ${rule.isActive ? '' : 'opacity-60'}`}>
                        <td className="px-3 py-2">{rule.priority}</td>
                        <td className="px-3 py-2">{categoryLabel(rule.category)}</td>
                        <td className="px-3 py-2 font-medium">{rule.title}</td>
                        <td className="px-3 py-2 text-muted-foreground">{rule.instruction}</td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={rule.isActive}
                            onChange={() => handleToggleActive(rule)}
                            aria-label={`Activa: ${rule.title}`}
                            className="h-4 w-4"
                          />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {confirmDeleteId === rule.id ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-red-700">¿Eliminar?</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                disabled={isDeleting}
                                onClick={() => handleDelete(rule.id)}
                              >
                                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sí'}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={isDeleting}
                                onClick={() => setConfirmDeleteId(null)}
                              >
                                No
                              </Button>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => openEdit(rule)}>
                                <Pencil className="h-4 w-4 mr-1" />
                                Editar
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirmDeleteId(rule.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Eliminar
                              </Button>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </CardContent>

      {form && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <h3 className="text-lg font-semibold">
              {editingId ? 'Editar regla' : 'Nueva regla'} — Nivel {form.level}
            </h3>

            {formError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                <AlertCircle className="h-4 w-4" />
                <p>{formError}</p>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="rule-category" className="text-sm font-medium">Categoría</label>
              <select
                id="rule-category"
                className={selectClass}
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES[form.level].map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="rule-title" className="text-sm font-medium">Título</label>
              <Input
                id="rule-title"
                value={form.title}
                maxLength={150}
                required
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="rule-instruction" className="text-sm font-medium">Instrucción</label>
              <textarea
                id="rule-instruction"
                className={`${selectClass} h-28`}
                value={form.instruction}
                maxLength={500}
                required
                onChange={(e) => setForm({ ...form, instruction: e.target.value })}
              />
              <p className="text-xs text-muted-foreground text-right">{form.instruction.length}/500</p>
            </div>

            <div className="space-y-2">
              <label htmlFor="rule-priority" className="text-sm font-medium">Prioridad (1–1000, 1 = más importante)</label>
              <Input
                id="rule-priority"
                type="number"
                min={1}
                max={1000}
                step={1}
                value={Number.isNaN(form.priority) ? '' : form.priority}
                onChange={(e) =>
                  setForm({ ...form, priority: e.target.value === '' ? NaN : parseInt(e.target.value, 10) })
                }
                required
              />
            </div>

            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Activa
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeForm} disabled={isSaving}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving || Number.isNaN(form.priority)}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Guardar
              </Button>
            </div>
          </form>
        </div>
      )}
    </Card>
  );
}
